/**
 * Marquee
 *
 * Seamless looping scroll of a repeated track, built on Motion (free, #7780 R-V9).
 * The track is duplicated once; `useAnimationFrame` drives a percentage x motion
 * value from 0% to -50%, so the two halves swap seamlessly and the loop has no seam.
 *
 * Behaviour:
 *   - seamless loop: the x motion value cycles 0% -> -50% in one lap;
 *   - hover pause: the frame callback stops accumulating time while the pointer is
 *     over the track, so the content freezes at its current position and resumes
 *     from exactly there;
 *   - reduced motion: the frame callback never runs (useReducedMotion);
 *   - token-driven speed: a `speed` prop in seconds per half-track lap (default 30,
 *     matching the old `.animate-marquee` rule).
 *
 * Usage:
 *   import { Marquee } from "@sbs/ui-core";
 *
 *   <Marquee aria-label="Technology stack">
 *     {["React", "Vite", "Workers", "D1"].map((item) => (
 *       <span key={item} className="mx-8 text-sm">{item}</span>
 *     ))}
 *   </Marquee>
 */

import * as React from "react";
import { motion, useAnimationFrame, useMotionValue, useReducedMotion } from "motion/react";
import { cn } from "../../lib/utils";

export interface MarqueeProps extends React.ComponentPropsWithoutRef<"div"> {
  children: React.ReactNode;
  /** Seconds for one half-track lap (default 30, matching the old CSS rule). */
  speed?: number;
}

export function Marquee({ children, speed = 30, className, ...rest }: MarqueeProps) {
  const prefersReduced = useReducedMotion() ?? false;
  const x = useMotionValue("0%");
  const elapsedRef = React.useRef(0);
  const pausedRef = React.useRef(false);

  useAnimationFrame((_time, delta) => {
    if (prefersReduced || pausedRef.current) return; // reduced motion or hover: freeze
    elapsedRef.current += delta;
    const cycle = (elapsedRef.current / (speed * 1000)) % 1;
    x.set(`${-(cycle * 50)}%`);
  });

  return (
    <div
      className={cn("group/marquee overflow-hidden whitespace-nowrap", className)}
      onPointerEnter={() => {
        if (!prefersReduced) pausedRef.current = true;
      }}
      onPointerLeave={() => {
        pausedRef.current = false;
      }}
      {...rest}
    >
      <motion.div className="inline-flex w-max" style={{ x }}>
        {children}
        {/* The duplicated half makes the 0% -> -50% lap seamless. */}
        <div aria-hidden="true" className="inline-flex">
          {children}
        </div>
      </motion.div>
    </div>
  );
}

export default Marquee;
